import { useMemo } from "react";
import { IonList } from "@ionic/react";
import type { ITask } from "../types";
import { useStore } from "../store/useStore";
import { groupTasksIntoStacks } from "../lib/dependency-helpers";
import { SwipeableItem, type SwipeAction } from "./SortableTaskList";
import TaskStack from "./TaskStack";

type SwipeStartProp = SwipeAction | ((task: ITask) => SwipeAction | null);

interface GroupedTaskListProps {
  tasks: ITask[];
  /** When true, tasks only stack visually if 2+ linked tasks appear in `tasks`. */
  stackOnlyIfMultiVisible?: boolean;
  showMoveToToday?: boolean;
  showMoveToBacklog?: boolean;
  showComplete?: boolean;
  showStatus?: boolean;
  swipeStart?: SwipeStartProp;
  swipeEnd?: SwipeAction;
  swipeEndSecondary?: SwipeAction | ((task: ITask) => SwipeAction | null);
}

/**
 * Renders a list of tasks, grouping tasks connected by dependencies into stacks.
 * Preserves the original sort order of the input `tasks` array by anchoring each
 * stack at the position of its "root" task (or the first task belonging to the
 * stack encountered).
 */
export default function GroupedTaskList({
  tasks,
  stackOnlyIfMultiVisible = false,
  showMoveToToday,
  showMoveToBacklog,
  showComplete,
  showStatus,
  swipeStart,
  swipeEnd,
  swipeEndSecondary,
}: GroupedTaskListProps) {
  const dependencies = useStore((s) => s.dependencies);
  const allTasks = useStore((s) => s.tasks);

  const { renderItems } = useMemo(() => {
    const { stacks, taskToStack } = groupTasksIntoStacks(
      tasks,
      dependencies,
      allTasks
    );

    // Build render sequence preserving incoming task order.
    // When a task belongs to a stack, emit the whole stack at its first-encountered position.
    const emittedStacks = new Set<string>();
    const items: Array<
      | { kind: "task"; task: ITask }
      | { kind: "stack"; anchor: string; stackTasks: ITask[]; eligible: Set<string> }
    > = [];

    const byUuid = new Map(allTasks.map((t) => [t.uuid, t]));

    for (const task of tasks) {
      const stackKey = taskToStack.get(task.uuid);
      if (!stackKey) {
        items.push({ kind: "task", task });
        continue;
      }
      const group = stacks.get(stackKey)!;

      // If stackOnlyIfMultiVisible: only group if 2+ of the stack's tasks are in `tasks`
      const visibleUuidSet = new Set(tasks.map((t) => t.uuid));
      const visibleCount = group.taskUuids.filter((u) => visibleUuidSet.has(u)).length;
      if (stackOnlyIfMultiVisible && visibleCount < 2) {
        items.push({ kind: "task", task });
        continue;
      }

      if (emittedStacks.has(stackKey)) continue;
      emittedStacks.add(stackKey);

      const stackTasks = group.taskUuids
        .map((u) => byUuid.get(u))
        .filter((t): t is ITask => !!t);
      items.push({
        kind: "stack",
        anchor: stackKey,
        stackTasks,
        eligible: new Set(group.eligibleUuids),
      });
    }

    return { renderItems: items };
  }, [tasks, dependencies, allTasks, stackOnlyIfMultiVisible]);

  return (
    <IonList
      lines="none"
      style={{ background: "transparent", padding: 0 }}
      className="space-y-2"
    >
      {renderItems.map((item) => {
        if (item.kind === "task") {
          return (
            <SwipeableItem
              key={item.task.uuid}
              task={item.task}
              showMoveToToday={showMoveToToday}
              showMoveToBacklog={showMoveToBacklog}
              showComplete={showComplete}
              showStatus={showStatus}
              swipeStart={swipeStart}
              swipeEnd={swipeEnd}
              swipeEndSecondary={swipeEndSecondary}
            />
          );
        }
        return (
          <TaskStack
            key={item.anchor}
            tasks={item.stackTasks}
            eligibleUuids={item.eligible}
            showMoveToToday={showMoveToToday}
            showMoveToBacklog={showMoveToBacklog}
            showComplete={showComplete}
            showStatus={showStatus}
            swipeStart={swipeStart}
            swipeEnd={swipeEnd}
            swipeEndSecondary={swipeEndSecondary}
          />
        );
      })}
    </IonList>
  );
}
